import React, { useState, useEffect, useCallback } from 'react';
import { useAlert, positions } from 'react-alert';
import Cookies from 'js-cookie';
import axios from 'axios';
import regenerateToken from '../../regenerateToken';
import Loader from '../../loader/loader';
import AppLayout from '../../layout/AppLayout';
import Dashboard from './Dashboard';
import EditorWorkspace from './EditorWorkspace';
import { buildRequestConfig } from '../../../utils/apiHelper';
import { isAdmin as checkAdmin } from '../../../utils/authUtils';
import './redesign.css';

const ParameterContentManager = ({ version, setEditVersion }) => {
    const alert = useAlert();
    const [loading, setLoading] = useState(true);
    const [parameterContent, setParameterContent] = useState({});

    // Navigation State
    const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard' | 'editor'
    const [activeParameter, setActiveParameter] = useState(null);
    const [activeSection, setActiveSection] = useState('text'); // 'text', 'tips', 'causes', etc.

    // Version management - memoized and safe localStorage parsing
    const storedVersions = React.useMemo(() => {
        try {
            const data = localStorage.getItem('versionData');
            if (!data) return [];
            return JSON.parse(data);
        } catch (error) {
            console.error('Failed to parse versionData from localStorage:', error);
            return [];
        }
    }, []);

    const allVersions = storedVersions?.length
        ? [...storedVersions.map(v => v.name), 'live']
        : ['live'];

    // Fetch Content
    const getExistingParameterContent = useCallback(async () => {
        if (checkAdmin()) {
            return await axios({
                ...buildRequestConfig({
                    method: 'GET',
                    url: 'https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/content/parameter',
                    extraParams: { version },
                }),
                validateStatus: (status) => status >= 200 && status <= 401,
            });
        }
        const idToken = Cookies.get("idToken");
        const configBody = {
            headers: {
                authorization: idToken,
            },
            method: "GET",
            url: `https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/content/parameter?version=${version}`,
            validateStatus: function (status) {
                return status >= 200 && status <= 401;
            },
        };
        return await axios(configBody);
    }, [version]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            let response = await getExistingParameterContent();

            if (response.status === 401) {
                await regenerateToken();
                response = await getExistingParameterContent();
            }

            if (response.data && response.data.isSuccess) {
                let content = response.data.data;

                // RECOVERY FIX: Detect if data was saved with wrong structure
                // If the content has a 'data' key containing objects AND a 'version' key,
                // it means the save function previously sent the wrong payload format.
                // Extract the actual parameters from inside the nested 'data' key.
                if (content &&
                    typeof content === 'object' &&
                    content.data &&
                    typeof content.data === 'object' &&
                    content.version !== undefined) {
                    console.warn("Detected corrupted data structure. Recovering actual content from nested 'data' key.");
                    content = content.data;
                }

                setParameterContent(content);
            } else {
                alert.error("Failed to load parameter content");
            }
        } catch (error) {
            console.error("Error fetching parameters:", error);
            alert.error("Something went wrong while fetching data");
        } finally {
            setLoading(false);
        }
    }, [getExistingParameterContent, alert]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Handlers
    const handleSelectParameter = (paramName) => {
        setActiveParameter(paramName);
        setCurrentView('editor');
        // Default to 'text' section when opening a parameter
        setActiveSection('text');
    };

    const handleBackToDashboard = () => {
        setCurrentView('dashboard');
        setActiveParameter(null);
    };

    const handleSaveContent = async (updatedContent = null) => {
        // If updatedContent is passed (from editor update), update local state only
        // If not passed (from Save button click), persist to API

        if (updatedContent) {
            // Just update local state, don't save to API
            setParameterContent(updatedContent);
            return;
        }

        // Save Button Clicked - persist current state to API
        setLoading(true);
        try {
            // IMPORTANT: The API expects the content DIRECTLY as body, NOT wrapped in { data: ... }
            // The version goes in the URL query string, NOT in the body
            // See: VersionManagement.js lines 385-399 for reference
            const buildSaveConfig = () => {
                if (checkAdmin()) {
                    return {
                        ...buildRequestConfig({
                            method: 'POST',
                            url: 'https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/content/parameter',
                            data: parameterContent,
                            extraHeaders: { 'Content-Type': 'application/json' },
                            extraParams: { version: version || '' },
                        }),
                        validateStatus: (status) => (status >= 200 && status < 300) || status === 401,
                    };
                }
                const idToken = Cookies.get("idToken");
                return {
                    method: 'POST',
                    url: `https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/content/parameter?version=${version || ""}`,
                    headers: {
                        authorization: idToken,
                        'Content-Type': 'application/json'
                    },
                    data: parameterContent,
                    validateStatus: (status) => (status >= 200 && status < 300) || status === 401,
                };
            };

            let response = await axios(buildSaveConfig());

            if (response.status === 401) {
                await regenerateToken();
                response = await axios(buildSaveConfig());
            }

            if (response.data && response.data.isSuccess) {
                alert.success("Content saved successfully!");
            } else {
                alert.error("Failed to save content.");
            }
        } catch (error) {
            console.error("Save error:", error);
            alert.error("Error saving content.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <AppLayout>
            {loading ? (
                <Loader />
            ) : (
                <div className="pcr-container">
                    {currentView === 'dashboard' ? (
                        <Dashboard
                            parameterContent={parameterContent}
                            onSelectParameter={handleSelectParameter}
                            version={version}
                            allVersions={allVersions}
                            setEditVersion={setEditVersion}
                        />
                    ) : (
                        <EditorWorkspace
                            parameterContent={parameterContent}
                            activeParameter={activeParameter}
                            activeSection={activeSection}
                            onChangeSection={setActiveSection}
                            onChangeParameter={setActiveParameter}
                            onBack={handleBackToDashboard}
                            onUpdateContent={(newData) => handleSaveContent(newData)}
                            onSave={() => handleSaveContent()}
                        />
                    )}
                </div>
            )}
        </AppLayout>
    );
};

export default ParameterContentManager;
